import React from "react";
import { Box, Typography } from "@mui/material";
import { FONT, SECTION_TITLE_SX } from "./formStyles";

/**
 * The Online Policy form's section card (2026-10-23 redesign): a white card with the section's title and a one-line
 * description, then its fields. One look for every group of fields on the three steps.
 *
 * No `overflow: hidden`: the form's dropdowns (MUI Autocomplete with disablePortal) and date pickers (antd, popup in
 * the parent) open INSIDE the card, and a clipping card would cut them off.
 *
 *   title        the section's heading — kept word for word (tests and operators know these titles)
 *   description  one short line under the title (optional)
 *   action       something at the right of the header (optional)
 *   testId       data-testid of the card
 *   bodySx       extra styles for the body
 *   ref          the section element (e.g. to scroll it into view)
 */
const FormSection = React.forwardRef(({ title, description, action, testId, sx, bodySx, children }, ref) => (
  <Box component="section" className="op-section" data-testid={testId} sx={sx} ref={ref}>
    {title || action ? (
      <Box className="op-section-head">
        <Box sx={{ minWidth: 0 }}>
          {title ? <Typography sx={{ ...SECTION_TITLE_SX, fontSize: "15.5px" }}>{title}</Typography> : null}
          {description ? (
            <Typography sx={{ fontFamily: FONT, fontSize: "12.5px", color: "#667085", mt: 0.25, pl: "14px", lineHeight: 1.45 }}>
              {description}
            </Typography>
          ) : null}
        </Box>
        {action ? <Box sx={{ flexShrink: 0 }}>{action}</Box> : null}
      </Box>
    ) : null}
    <Box className="op-section-body" sx={bodySx}>
      {children}
    </Box>
  </Box>
));
FormSection.displayName = "FormSection";

/**
 * A group inside a section ("Nominee Details" in the PA Cover card, "Financier Details" in Vehicle details): a thin
 * rule and a smaller heading, so it reads as part of its card rather than a card of its own.
 */
export const FormSubsection = ({ title, description, testId, sx, children }) => (
  <Box className="op-subsection" data-testid={testId} sx={sx}>
    {title ? (
      <Typography sx={{ fontFamily: FONT, fontSize: "14px", fontWeight: 700, color: "#1E3A5F", lineHeight: "20px" }}>{title}</Typography>
    ) : null}
    {description ? (
      <Typography sx={{ fontFamily: FONT, fontSize: "12.5px", color: "#667085", mt: 0.25, lineHeight: 1.45 }}>{description}</Typography>
    ) : null}
    <Box sx={{ mt: title || description ? 1.5 : 0 }}>{children}</Box>
  </Box>
);

export default FormSection;
